import { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';

function PullToRefresh({ onRefresh, children }) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);
  const canPull = useRef(false);

  const threshold = 80;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      // Only allow pull-to-refresh when at the very top of the page
      if (window.scrollY === 0 && !isRefreshing) {
        startY.current = e.touches[0].clientY;
        canPull.current = true;
      } else {
        canPull.current = false;
      }
    };

    const handleTouchMove = (e) => {
      if (!canPull.current || !startY.current) return;

      const touchY = e.touches[0].clientY;
      const distance = touchY - startY.current;

      // Only activate pull if moving down and at top of page
      if (distance > 0 && window.scrollY === 0) {
        setIsPulling(true);
        setPullDistance(Math.min(distance * 0.5, threshold + 30)); // Reduce sensitivity
        
        // Only prevent default if actually pulling (prevents scroll blocking)
        if (distance > 50) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling) {
        canPull.current = false;
        startY.current = 0;
        return;
      }

      if (pullDistance >= threshold && !isRefreshing) {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh error:', error);
        }
        setTimeout(() => {
          setIsRefreshing(false);
          setIsPulling(false);
          setPullDistance(0);
        }, 800);
      } else {
        setIsPulling(false);
        setPullDistance(0);
      }
      
      canPull.current = false;
      startY.current = 0;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, pullDistance, onRefresh, isRefreshing]);

  const pullProgress = Math.min((pullDistance / threshold) * 100, 100);
  const shouldTrigger = pullDistance >= threshold;

  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Pull Indicator - Only show when actually pulling */}
      {(isPulling || isRefreshing) && pullDistance > 20 && (
        <div
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center bg-zinc-900/95 backdrop-blur-sm border-b border-white/10 transition-all"
          style={{
            height: `${Math.min(pullDistance, 70)}px`,
            opacity: Math.min(pullDistance / 40, 1),
          }}
        >
          <div className="flex flex-col items-center">
            <RefreshCw
              className={`w-5 h-5 text-[#E11D48] transition-transform ${
                isRefreshing ? 'animate-spin' : ''
              }`}
              style={{
                transform: isRefreshing ? '' : `rotate(${pullProgress * 3.6}deg)`,
              }}
            />
            <p className="text-white text-xs uppercase tracking-widest mt-1">
              {isRefreshing ? 'Refreshing...' : shouldTrigger ? 'Release' : 'Pull'}
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      {children}
    </div>
  );
}

export default PullToRefresh;
